import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import ErrorIcon from '@material-ui/icons/Error';

import useStore from '@hooks/useStore';
import Input from '@components/common/Input';
import MaskedInput from '@components/common/MaskedInput';
import PayButton from '@components/PayButton';
import { IBelvoPaymentData, PartnerFormProps } from '@containers/checkout/payment-form/types';
import { isBankAccountNumberValid } from '@utils/validators';
import useStyles from './styles';
import { FieldValues } from 'react-hook-form/dist/types';
import { privacyLink, termsLink } from '@configs/variables';
import FormPartnerLogo from '@components/FormPartnerLogo';
import { Partner } from '@constants/paymentLink';

const BelvoDirectDebitForm: React.FC<PartnerFormProps> = ({ onValidationError, apiKey }) => {
  const {
    belvo,
    checkout,
    links: {
      paymentLink: { paymentLinkId, total: amount, isSubscription, subscription },
      coBrandingConfig: { primaryColor, secondaryColor },
    },
  } = useStore();

  const css = useStyles({ primaryColor, secondaryColor });

  const { t } = useTranslation();
  const { control, formState, getValues, handleSubmit } = useFormContext();

  const DirectDebitPaymentError: React.FC = () => (
    <Grid item className={css.paymentError}>
      <ErrorIcon />{' '}
      {belvo?.errors?.bankAccount?.message !== ''
        ? belvo?.errors?.bankAccount?.message
        : t('checkoutScreen.payment.errors.card.unknown')}
    </Grid>
  );

  const createDirectDebitSubscription = (values: IBelvoPaymentData) => {
    const data: IBelvoPaymentData = {
      ...values,
      paymentLinkId,
      amount: +amount,
    };

    belvo.createDirectDebitSubscription(data);
  };

  useEffect(() => {
    const data = getValues();
    if (data?.bankAccountNumber) {
      if (data.bankAccountNumber.length === 18) {
        belvo.validateBankAccountNumberData(data.bankAccountNumber);
      }
    }
  }, [formState.isValidating]);

  return (
    <>
      <Grid container spacing={2}>
        {belvo.hasBankAccountErrors && <DirectDebitPaymentError />}
        <Grid item xs={12}>
          <Controller
            name="documentNumber"
            control={control}
            defaultValue={subscription?.payerTaxId || null}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                fullWidth
                autoComplete="off"
                label={t('checkoutScreen.payment.methods.directDebit.bankAccount.documentType')}
                disabled={formState.isSubmitting || subscription?.payerTaxId !== ''}
                error={fieldState.invalid || belvo.hasDirectDebitSubscriptionProcessErrors}
                inputProps={{
                  type: 'text',
                  autoComplete: 'off',
                  maxLength: 13,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="bankAccountNumber"
            control={control}
            defaultValue={''}
            rules={{ required: true, validate: { bankAccountNumber: isBankAccountNumberValid } }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                label={t('checkoutScreen.payment.methods.directDebit.bankAccount.clabe')}
                autoComplete="off"
                inputProps={{
                  inputMode: 'numeric',
                  guide: true,
                  mask: '999 999 99999999999 9',
                }}
                onChange={(e) => {
                  field.onChange(e.target.value.replace(/\s/g, ''));
                }}
                disabled={formState.isSubmitting}
                error={
                  fieldState.invalid ||
                  belvo.hasDirectDebitSubscriptionProcessErrors ||
                  belvo.hasBankAccountErrors
                }
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Input
            value={belvo.bankAccount.bankName}
            fullWidth
            autoComplete="off"
            label={t('checkoutScreen.payment.methods.directDebit.bankAccount.bank')}
            disabled={true}
            error={belvo.hasDirectDebitSubscriptionProcessErrors}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <div className={css.noteInfo}>
          {t('checkoutScreen.payment.methods.directDebit.noteInfo')}
        </div>
      </Grid>
      <PayButton
        customDisclaimer={
          <div className={css.disclaimer}>
            <Trans i18nKey={'checkoutScreen.payment.methods.disclaimerDirectDebitSubscription'}>
              <strong />
              <a href={termsLink} target="_blank" rel="noreferrer noopener" />
              <a href={privacyLink} target="_blank" rel="noreferrer noopener" />
            </Trans>
          </div>
        }
        onClick={handleSubmit(
          createDirectDebitSubscription as SubmitHandler<FieldValues>,
          onValidationError,
        )}
        disabled={belvo.isProcessing || !checkout.isAllowedToPayWithDirectDebit}
        loading={belvo.isProcessing}
        isSubscription={isSubscription}
        label={
          !isSubscription
            ? t('checkoutScreen.payment.methods.submit')
            : t('checkoutScreen.payment.methods.subscribe')
        }
      />
      <FormPartnerLogo partner={Partner.BELVO} />
    </>
  );
};

export default observer(BelvoDirectDebitForm);
